import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from 'shared/apiClient';
import { useSupport, getSupportHistory } from '../../contexts/SupportContext';
import type { Role } from '../../shared/permissions';
import './SupportPanel.css';

interface SupportOrg {
  id: string;
  name: string;
  slug: string;
  display_name?: string;
  is_demo?: boolean;
  created_at: string;
}

interface DuplicateJobProgress {
  percent?: number;
  table?: string;
  tableCopied?: number;
  tableTotal?: number;
  tablesDone?: number;
  tablesTotal?: number;
}

interface DuplicateJob {
  id: string;
  source_organization_id: string;
  target_organization_id: string | null;
  status: 'queued' | 'running' | 'verifying' | 'completed' | 'failed' | 'rolled_back';
  phase?: string | null;
  progress?: DuplicateJobProgress;
  error?: string | null;
  created_at: string;
  options?: { targetName?: string | null };
}

interface SupportPanelProps {
  onClose: () => void;
}

const DEMO_COMPANY_NAMES = [
  'Aurora Labs',
  'Nimbus Health',
  'Vertex Finance',
  'Bluepine Retail',
  'Helios Energy',
  'Cascade Logistics',
];

function isJobActive(job: DuplicateJob): boolean {
  return job.status === 'queued' || job.status === 'running' || job.status === 'verifying';
}

export function SupportPanel({ onClose }: SupportPanelProps) {
  const { searchOrgs, enterSupportMode, supportMode } = useSupport();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SupportOrg[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<SupportOrg | null>(null);
  const [role, setRole] = useState<Role>('administrator');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const history = getSupportHistory();

  // ---- Duplicate-as-demo state ----
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [demoName, setDemoName] = useState('');
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [startingDuplicate, setStartingDuplicate] = useState(false);

  const doSearch = useCallback(
    async (q: string) => {
      if (!q.trim()) {
        setResults([]);
        return;
      }
      setSearching(true);
      setError(null);
      try {
        const orgs = await searchOrgs(q);
        setResults(orgs);
      } catch {
        setError('Search failed');
      } finally {
        setSearching(false);
      }
    },
    [searchOrgs]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, doSearch]);

  // Resume any running clone when the panel opens.
  const activeJobsQuery = useQuery<DuplicateJob[], Error>({
    queryKey: ['/api/support/duplicate-jobs', 'active'],
    queryFn: async () => {
      const res = await apiFetch('/api/support/duplicate-jobs?active=1');
      if (!res.ok) throw new Error(`API ${res.status}`);
      return res.json();
    },
    staleTime: 0,
  });

  useEffect(() => {
    const jobs = activeJobsQuery.data;
    if (!jobs || activeJobId) return;
    const running = jobs.find((j) => isJobActive(j));
    if (running) setActiveJobId(running.id);
  }, [activeJobsQuery.data, activeJobId]);

  // Poll the tracked job; refetchInterval stops automatically once terminal.
  const jobQuery = useQuery<DuplicateJob, Error>({
    queryKey: ['/api/support/duplicate-jobs', activeJobId],
    queryFn: async () => {
      const res = await apiFetch(`/api/support/duplicate-jobs/${activeJobId}`);
      if (!res.ok) throw new Error(`API ${res.status}`);
      return res.json() as Promise<DuplicateJob>;
    },
    enabled: !!activeJobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && !isJobActive(data)) return false;
      return 2000;
    },
    staleTime: 0,
  });

  const job = jobQuery.data || null;
  const activeJobForSelected =
    selectedOrg && job && job.source_organization_id === selectedOrg.id && isJobActive(job)
      ? job
      : null;
  const activeJobAnySource =
    (activeJobsQuery.data || []).find((j) => isJobActive(j)) ||
    (job && isJobActive(job) ? job : null);
  const selectedHasActiveJob = !!(
    selectedOrg &&
    ((activeJobsQuery.data || []).some(
      (j) => j.source_organization_id === selectedOrg.id && isJobActive(j)
    ) ||
      activeJobForSelected)
  );

  const openConfirm = () => {
    if (!selectedOrg) return;
    const company = DEMO_COMPANY_NAMES[Math.floor(Math.random() * DEMO_COMPANY_NAMES.length)];
    setDemoName(`${company} (Demo)`);
    setDuplicateError(null);
    setConfirmOpen(true);
  };

  const startDuplicate = async () => {
    if (!selectedOrg) return;
    setStartingDuplicate(true);
    setDuplicateError(null);
    try {
      const res = await apiFetch(`/api/support/organizations/${selectedOrg.id}/duplicate`, {
        method: 'POST',
        body: JSON.stringify({ targetName: demoName.trim() || undefined }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 409) {
        setDuplicateError('A clone of this organization is already in progress.');
        if (body.jobId) {
          setConfirmOpen(false);
          setActiveJobId(body.jobId);
        }
        return;
      }
      if (!res.ok) {
        setDuplicateError(body.error || `Failed to start duplication (${res.status})`);
        return;
      }
      setConfirmOpen(false);
      setActiveJobId(body.jobId);
      activeJobsQuery.refetch();
    } catch {
      setDuplicateError('Failed to start duplication');
    } finally {
      setStartingDuplicate(false);
    }
  };

  const handleEnter = () => {
    if (!selectedOrg) return;
    enterSupportMode(selectedOrg, role);
  };

  const enterClonedOrg = () => {
    if (!job || !job.target_organization_id) return;
    const displayName = job.options?.targetName || 'Demo organization';
    enterSupportMode(
      {
        id: job.target_organization_id,
        name: displayName,
        slug: '',
        display_name: displayName,
        is_demo: true,
        created_at: '',
      },
      'owner'
    );
  };

  const progress = job?.progress || {};
  const percent = typeof progress.percent === 'number' ? progress.percent : 0;

  return (
    <div className="support-panel-overlay" onClick={onClose}>
      <div className="support-panel" onClick={(e) => e.stopPropagation()}>
        <div className="support-panel-header">
          <h3>Support Mode</h3>
          <button className="support-panel-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="support-panel-body">
          {job ? (
            <div className="support-dup-job">
              {isJobActive(job) ? (
                <>
                  <label className="support-label">Duplicating organization</label>
                  <div className="support-dup-progress-track">
                    <div
                      className="support-dup-progress-bar"
                      style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
                    />
                  </div>
                  <div className="support-hint">
                    {job.status === 'verifying'
                      ? 'Verifying anonymization...'
                      : progress.table
                        ? `Copying ${progress.table} (${progress.tableCopied ?? 0} / ${progress.tableTotal ?? 0})...`
                        : 'Queued...'}
                  </div>
                  <div className="support-hint">
                    {percent}% — you can close this panel; the job keeps running.
                  </div>
                </>
              ) : job.status === 'completed' ? (
                <>
                  <label className="support-label">Duplicate ready</label>
                  <div className="support-hint">
                    Demo organization created and verified (no source data leaked).
                  </div>
                  <button className="support-enter-btn" onClick={enterClonedOrg}>
                    Enter organization
                  </button>
                  <button className="support-dup-secondary" onClick={() => setActiveJobId(null)}>
                    Back to search
                  </button>
                </>
              ) : (
                <>
                  <label className="support-label">
                    {job.status === 'rolled_back' ? 'Duplicate rolled back' : 'Duplicate failed'}
                  </label>
                  <div className="support-error">
                    {job.status === 'rolled_back'
                      ? 'Verification found source data in the copy; the clone was deleted.'
                      : job.error || 'The duplication job failed.'}
                  </div>
                  <button
                    className="support-enter-btn"
                    onClick={() => {
                      setActiveJobId(null);
                      openConfirm();
                    }}
                    disabled={!selectedOrg}
                  >
                    Retry
                  </button>
                  <button className="support-dup-secondary" onClick={() => setActiveJobId(null)}>
                    Back to search
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              <label className="support-label">Search organization</label>
              <input
                className="support-input"
                type="text"
                placeholder="Name, slug, or UUID..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />

              {searching && <div className="support-hint">Searching...</div>}
              {error && <div className="support-error">{error}</div>}

              {results.length > 0 && (
                <ul className="support-results">
                  {results.map((org) => (
                    <li
                      key={org.id}
                      className={`support-result-item ${selectedOrg?.id === org.id ? 'selected' : ''}`}
                      onClick={() => setSelectedOrg(org)}
                    >
                      <span className="support-result-name">
                        {org.display_name || org.name}
                        {org.is_demo ? <span className="support-demo-badge">Demo</span> : null}
                      </span>
                      <span className="support-result-slug">{org.slug}</span>
                    </li>
                  ))}
                </ul>
              )}

              {query && !searching && results.length === 0 && !error && (
                <div className="support-hint">No organizations found</div>
              )}

              <label className="support-label">Role</label>
              <div className="support-role-picker">
                <label className={`support-role-option ${role === 'owner' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="support-role"
                    value="owner"
                    checked={role === 'owner'}
                    onChange={() => setRole('owner')}
                  />
                  Owner
                </label>
                <label
                  className={`support-role-option ${role === 'administrator' ? 'active' : ''}`}
                >
                  <input
                    type="radio"
                    name="support-role"
                    value="administrator"
                    checked={role === 'administrator'}
                    onChange={() => setRole('administrator')}
                  />
                  Administrator
                </label>
                <label className={`support-role-option ${role === 'member' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="support-role"
                    value="member"
                    checked={role === 'member'}
                    onChange={() => setRole('member')}
                  />
                  Member
                </label>
              </div>

              {history.length > 0 && (
                <div className="support-history">
                  <label className="support-label">Recent</label>
                  <ul className="support-history-list">
                    {history.slice(0, 5).map((entry, i) => (
                      <li
                        key={i}
                        className="support-history-item"
                        onClick={() => {
                          setSelectedOrg({
                            id: entry.orgId,
                            name: entry.orgName,
                            slug: '',
                            created_at: '',
                          });
                          setRole(
                            entry.role === 'owner' ||
                              entry.role === 'administrator' ||
                              entry.role === 'member'
                              ? entry.role
                              : 'administrator'
                          );
                        }}
                      >
                        {entry.orgName} <span className="support-history-role">({entry.role})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                className="support-enter-btn"
                disabled={!selectedOrg || supportMode}
                onClick={handleEnter}
              >
                {supportMode ? 'Already in support mode' : 'Enter organization'}
              </button>

              {selectedOrg && !selectedOrg.is_demo ? (
                <button
                  className="support-dup-btn"
                  disabled={selectedHasActiveJob}
                  title={
                    selectedHasActiveJob
                      ? 'A clone of this organization is already in progress'
                      : 'Create an anonymized full copy for demos'
                  }
                  onClick={openConfirm}
                >
                  Duplicate as demo
                </button>
              ) : null}
              {selectedOrg?.is_demo ? (
                <div className="support-hint">Demo organizations cannot be cloned again.</div>
              ) : null}
              {activeJobAnySource && !selectedHasActiveJob ? (
                <button
                  className="support-dup-secondary"
                  onClick={() => setActiveJobId(activeJobAnySource.id)}
                >
                  View running duplicate job
                </button>
              ) : null}
            </>
          )}
        </div>

        {confirmOpen && selectedOrg ? (
          <div className="support-dup-confirm-overlay" onClick={() => setConfirmOpen(false)}>
            <div className="support-dup-confirm" onClick={(e) => e.stopPropagation()}>
              <h4>Duplicate as demo</h4>
              <label className="support-label" htmlFor="support-dup-name">
                Demo organization name
              </label>
              <input
                id="support-dup-name"
                className="support-input"
                type="text"
                value={demoName}
                onChange={(e) => setDemoName(e.target.value)}
              />
              <div className="support-dup-warning">
                Creates a full anonymized copy of{' '}
                <strong>{selectedOrg.display_name || selectedOrg.name}</strong>: fictional people
                and issue keys, no credentials, members, or logos. Jira/SharePoint sync is blocked
                server-side. Large organizations can take tens of minutes — you can close this panel
                while it runs.
              </div>
              {duplicateError ? <div className="support-error">{duplicateError}</div> : null}
              <div className="support-dup-actions">
                <button
                  className="support-dup-secondary"
                  onClick={() => setConfirmOpen(false)}
                  disabled={startingDuplicate}
                >
                  Cancel
                </button>
                <button
                  className="support-enter-btn"
                  onClick={startDuplicate}
                  disabled={startingDuplicate}
                >
                  {startingDuplicate ? 'Starting...' : 'Start duplication'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
